import { useEffect, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import api, { errorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import {
  Wand2, Loader2, Coins, FolderOpen, FolderCheck, Download, Sparkles, AlertTriangle, Cloud, CloudOff, Lock, Volume2, VolumeX,
} from "lucide-react";
import { Link } from "react-router-dom";
import { RefModePicker } from "./RefModePicker";
import {
  isFsSupported, ensureFolder, connectFolder, saveToFolder, dataUrlToBlob,
} from "../lib/localFolder";
import { FramePicker, RefPicker } from "./FramePicker";

const L = {
  it: {
    title_img: "Genera immagine", title_vid: "Genera video",
    credits: "crediti disp.", engine: "Motore di generazione", aspect: "Formato", resolution: "Risoluzione", duration: "Durata",
    audio_label: "Audio", audio_on: "Con audio", audio_off: "Senza audio", audio_hint: "L'audio aumenta i crediti. Di base i video sono muti.", audio_hint_free: "L'audio non cambia i crediti su questo modello. Di base i video sono muti.",
    folder_connected: "Cartella collegata", folder_connect: "Collega cartella",
    folder_hint: "Collega una cartella per salvare automaticamente (nessun costo cloud).",
    folder_unsupported: "Il salvataggio in cartella locale funziona solo su Chrome/Edge desktop; qui potrai scaricare manualmente.",
    generate: "Genera", generating: "Generazione…", generating_vid: "Generazione video… (può richiedere 1-3 minuti)",
    credits_word: "crediti", credit_one: "credito", saved_local: "Salvato in cartella locale", save_failed: "Salvataggio locale non riuscito",
    download: "Scarica", not_enough: "Crediti insufficienti", generated_ok: "Immagine generata", generated_vid_ok: "Video generato", vid_fetch_failed: "Video pronto in Galleria, ma non è stato possibile scaricarlo qui", vid_in_gallery: "Video pronto: aprilo dalla Galleria", save_to_folder: "Salva nella cartella",
    linked: "Collegato a questo prompt", failed: "Generazione non riuscita", seconds: "s",
    prompt_required: "Inserisci un prompt", prompt_label: "Prompt esterno", prompt_ph: "Incolla qui il prompt per generare immagine o video…",
    mode_image: "Immagine", mode_video: "Video",
    dest: "Salva su", dest_local: "Cartella", dest_drive: "Google Drive",
    dest_vixelforge: "Vixelforge", dest_vixelforge_hint: "Salvataggio automatico sul tuo spazio Vixelforge. Da mobile la cartella locale non è disponibile.",
    saved_vixelforge: "Salvato su Vixelforge",
    connect_drive: "Collega Drive", drive_connected: "Drive collegato", saved_drive: "Salvato su Google Drive",
    drive_opening: "Apro l'autorizzazione Google Drive… autorizza e torna qui.",
    refs: "Immagini di riferimento (opzionale)", refs_hint: "Fino a {n} immagini per mantenere coerenti soggetti e ambienti. Le immagini caricate vengono eliminate entro 24 ore.", refs_unsupported: "Questo modello non supporta immagini di riferimento: per la coerenza di soggetti e ambienti usa Nano Banana 2.1, Nano Banana Pro o GPT Image 2.5.",
    frame_from_video: "Dal video", frame_pick_video: "Scegli un video dalla Gallery", frame_grab_title: "Estrai un fotogramma", frame_grab_hint: "Metti in pausa sul fotogramma che ti serve (o usa i pulsanti primo/ultimo), poi premi Usa questo fotogramma.", frame_grab_first: "Primo fotogramma", frame_grab_last: "Ultimo fotogramma", frame_grab_other: "Altro video", frame_grab_use: "Usa questo fotogramma", needs_start: "serve frame iniziale", frames: "Frame video (opzionale)", frame_start: "Frame iniziale", frame_end: "Frame finale",
    frame_upload: "Carica", frame_from_gallery: "Dalla Gallery", frame_pick_gallery: "Scegli dalla Gallery",
    frame_gallery_empty: "Nessuna immagine salvata su Vixelforge.", frame_too_large: "Immagine troppo grande (max 10 MB)",
    frames_hint: "Il video partirà dal frame iniziale e terminerà su quello finale. Le immagini caricate vengono eliminate dopo la generazione.",
    frames_hint_noend: "Questo modello supporta solo il frame iniziale. Le immagini caricate vengono eliminate dopo la generazione.",
    frames_unsupported: "Questo modello non supporta i frame.",
    trial_locked: "Con un piano", trial_note: "Prova gratuita: puoi usare i modelli da 1 credito. Le immagini restano in Galleria per {d} giorni.", trial_video: "La generazione video è inclusa nei piani a pagamento.", trial_cta: "Scopri i piani",
  },
  en: {
    title_img: "Generate image", title_vid: "Generate video",
    credits: "credits avail.", engine: "Generation engine", aspect: "Aspect ratio", resolution: "Resolution", duration: "Duration",
    audio_label: "Audio", audio_on: "With audio", audio_off: "Muted", audio_hint: "Audio raises the credit cost. Videos are muted by default.", audio_hint_free: "Audio does not change the credit cost for this model. Videos are muted by default.",
    folder_connected: "Folder connected", folder_connect: "Connect folder",
    folder_hint: "Connect a folder to auto-save (no cloud cost).",
    folder_unsupported: "Local-folder save only works on Chrome/Edge desktop; here you can download manually.",
    generate: "Generate", generating: "Generating…", generating_vid: "Generating video… (may take 1-3 minutes)",
    credits_word: "credits", credit_one: "credit", saved_local: "Saved to local folder", save_failed: "Local save failed",
    download: "Download", not_enough: "Not enough credits", generated_ok: "Image generated", generated_vid_ok: "Video generated", vid_fetch_failed: "Video ready in Gallery, but it could not be downloaded here", vid_in_gallery: "Video ready: open it from the Gallery", save_to_folder: "Save to folder",
    linked: "Linked to this prompt", failed: "Generation failed", seconds: "s",
    prompt_required: "Enter a prompt", prompt_label: "External prompt", prompt_ph: "Paste a prompt here to generate an image or video…",
    mode_image: "Image", mode_video: "Video",
    dest: "Save to", dest_local: "Folder", dest_drive: "Google Drive",
    dest_vixelforge: "Vixelforge", dest_vixelforge_hint: "Auto-saved to your Vixelforge space. Local folder isn't available on mobile.",
    saved_vixelforge: "Saved to Vixelforge",
    connect_drive: "Connect Drive", drive_connected: "Drive connected", saved_drive: "Saved to Google Drive",
    drive_opening: "Opening Google Drive authorization… approve and come back.",
    refs: "Reference images (optional)", refs_hint: "Up to {n} images to keep subjects and environments consistent. Uploaded images are deleted within 24 hours.", refs_unsupported: "This model does not support reference images: for consistent subjects and environments use Nano Banana 2.1, Nano Banana Pro or GPT Image 2.5.",
    frame_from_video: "From video", frame_pick_video: "Pick a video from the Gallery", frame_grab_title: "Extract a frame", frame_grab_hint: "Pause on the frame you need (or use first/last), then press Use this frame.", frame_grab_first: "First frame", frame_grab_last: "Last frame", frame_grab_other: "Other video", frame_grab_use: "Use this frame", needs_start: "needs start frame", frames: "Video frames (optional)", frame_start: "Start frame", frame_end: "End frame",
    frame_upload: "Upload", frame_from_gallery: "From Gallery", frame_pick_gallery: "Pick from Gallery",
    frame_gallery_empty: "No images saved on Vixelforge.", frame_too_large: "Image too large (max 10 MB)",
    frames_hint: "The video starts on the start frame and ends on the end frame. Uploaded images are deleted after generation.",
    frames_hint_noend: "This model supports the start frame only. Uploaded images are deleted after generation.",
    frames_unsupported: "This model does not support frames.",
    trial_locked: "With a plan", trial_note: "Free trial: you can use 1-credit models. Images stay in your Gallery for {d} days.", trial_video: "Video generation is included in paid plans.", trial_cta: "See plans",
  },
};

const SESSION_KEY = "vxf_gen_session";
const stripFrame = (f) => (f ? { kind: f.kind, id: f.id } : null);

const ASPECT_TO_SIZE = { "16:9": "landscape_16_9", "21:9": "landscape_16_9", "3:2": "landscape_4_3", "4:3": "landscape_4_3", "1:1": "square_hd", "4:5": "portrait_4_3", "3:4": "portrait_4_3", "9:16": "portrait_16_9" };
const pickSize = (m, aspect) => {
  const sizes = m.sizes || [];
  if (aspect && sizes.includes(aspect)) return aspect;
  if (aspect && sizes.includes(ASPECT_TO_SIZE[aspect])) return ASPECT_TO_SIZE[aspect];
  return m.default_size;
};

export default function GenerateDialog({ open, onOpenChange, promptText, promptId, outputType = "image", editablePrompt = false, initialStart = null, initialEnd = null, initialRefs = null, initialRefMode = null, initialAspect = null, inline = false, seed = null }) {
  const { lang } = useI18n();
  const tt = L[lang] || L.en;

  const [vidMode, setVidMode] = useState(outputType === "video");
  const isVideo = vidMode;
  const [promptLocal, setPromptLocal] = useState(promptText || "");

  const [catalog, setCatalog] = useState(null);
  const [credits, setCredits] = useState(0);
  const [trial, setTrial] = useState(null);
  const [modelId, setModelId] = useState("");
  const [size, setSize] = useState("");          // image aspect/size
  const [resolution, setResolution] = useState("");
  const [duration, setDuration] = useState("");   // video
  const [aspect, setAspect] = useState("");        // video aspect
  const [audio, setAudio] = useState(false);       // video audio (off by default, costs less)
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);      // {image_data_url} | {video_url, objUrl}
  const [folderOn, setFolderOn] = useState(false);
  const folderRef = useRef(null);
  const [drive, setDrive] = useState({ configured: false, connected: false });
  const [dest, setDestState] = useState(() => localStorage.getItem("vxf_dest") || "vixelforge");
  const setDest = (d) => { setDestState(d); try { localStorage.setItem("vxf_dest", d); } catch { void 0; } };
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const [startFrame, setStartFrame] = useState(null);
  const [endFrame, setEndFrame] = useState(null);
  const [imgRefs, setImgRefs] = useState([]);
  const [refMode, setRefMode] = useState("edit");
  const [promptIdLocal, setPromptIdLocal] = useState(promptId || null);
  const srcRef = useRef("");
  const [hydrated, setHydrated] = useState(false);

  const supported = isFsSupported();
  const models = catalog ? (isVideo ? catalog.video : catalog.image) || [] : [];
  const model = models.find((m) => m.id === modelId);

  useEffect(() => {
    if (!open || !hydrated) return;
    const data = {
      src: srcRef.current, prompt: promptLocal, vid: vidMode, promptId: promptIdLocal, modelId, size, resolution, duration, aspect, audio,
      refs: imgRefs.map(stripFrame), refMode, start: stripFrame(startFrame), end: stripFrame(endFrame), ts: Date.now(),
    };
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch { void 0; }
  }, [open, hydrated, promptLocal, vidMode, promptIdLocal, modelId, size, resolution, duration, aspect, audio, imgRefs, refMode, startFrame, endFrame]);

  useEffect(() => {
    if (model?.resolutions?.length && !model.resolutions.includes(resolution)) setResolution(model.default_resolution || model.resolutions[0]);
    // eslint-disable-next-line
  }, [modelId, catalog]);

  // With reference images, switch to a model that supports them (Z-Image does not).
  useEffect(() => {
    if (isVideo || !catalog || !imgRefs.length || !model || model.max_refs) return;
    const alt = catalog.image.find((m) => m.max_refs);
    if (alt) { setModelId(alt.id); setSize(pickSize(alt, initialAspect)); setResolution(alt.default_resolution || ""); }
    // eslint-disable-next-line
  }, [catalog, imgRefs.length, modelId, isVideo]);

  useEffect(() => {
    if (open) {
      // Resume the last session unless the caller brings a new prompt.
      let sess = null;
      try { sess = JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { sess = null; }
      if (seed) sess = { ...seed, src: seed.key };
      const fresh = promptText && (!sess || sess.src !== promptText);
      if (fresh || !sess) {
        setPromptLocal(promptText || ""); setVidMode(outputType === "video"); setPromptIdLocal(promptId || null);
        setStartFrame(initialStart); setEndFrame(initialStart ? initialEnd : null);
        setImgRefs(initialRefs || []);
        setRefMode(initialRefMode || ((initialRefs || []).length && (initialRefs || []).every((r) => r.kind === "gallery") ? "retouch" : "edit"));
      } else {
        setPromptLocal(sess.prompt || ""); setVidMode(!!sess.vid); setPromptIdLocal(sess.promptId || null);
        setStartFrame(sess.start || null); setEndFrame(sess.end || null); setImgRefs(sess.refs || []);
        setRefMode(sess.refMode || ((sess.refs || []).length && (sess.refs || []).every((r) => r.kind === "gallery") ? "retouch" : "edit"));
        setModelId(sess.modelId || ""); setSize(sess.size || ""); setResolution(sess.resolution || "");
        setDuration(sess.duration || ""); setAspect(sess.aspect || ""); setAudio(!!sess.audio);
      }
      srcRef.current = fresh ? promptText : (sess?.src || promptText || "");
      setHydrated(true);
    } else {
      setHydrated(false);
    }
    // eslint-disable-next-line
  }, [open]);

  const videoCost = (m, dur, res, aud = false) => {
    if (!m) return 0;
    const secs = parseInt(String(dur || m.default_duration).replace("s", ""), 10) || 5;
    const mult = m.res_mult && res ? (m.res_mult[res] || 1) : 1;
    let cps = m.credits_per_second;
    if (m.supports_audio && !aud) cps = cps / (m.audio_mult || 1);
    return Math.max(1, Math.ceil(cps * secs * mult));
  };
  const imageCost = (m, res) => {
    if (!m) return 0;
    const mult = m.res_mult && res ? (m.res_mult[res] || 1) : 1;
    return Math.max(1, Math.ceil(m.credits * mult));
  };
  const variant = (isVideo && model?.res_variants?.[resolution]) || null;
  useEffect(() => {
    if (variant?.durations && !variant.durations.includes(duration)) setDuration(variant.durations[0]);
    if (variant?.frame_only && !startFrame && model) setResolution(model.default_resolution || "");
    // eslint-disable-next-line
  }, [resolution, startFrame, modelId]);
  const durationOpts = variant?.durations || model?.durations || [];
  const cost = model ? (isVideo ? videoCost(model, duration, resolution, audio) : imageCost(model, resolution)) : 0;
  const isLocked = (m) => !!trial && (isVideo ? videoCost(m, m.default_duration, m.default_resolution) : imageCost(m, m.default_resolution)) > trial.max;
  const trialBlocked = !!trial && cost > trial.max;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    (async () => {
      try {
        const { data } = await api.get("/generation/models");
        setCatalog({ image: data.image || [], video: data.video || [] });
        setCredits(data.credits_available || 0);
        setTrial(data.trial ? { max: data.trial_max_credits, days: data.trial_media_days } : null);
      } catch (err) { toast.error(errorMessage(err)); }
      try {
        const { data: ds } = await api.get("/drive/status");
        setDrive(ds);
        if (ds.connected && new URLSearchParams(window.location.search).get("drive") === "connected") setDest("drive");
      } catch { /* ignore */ void 0; }
      if (supported) {
        const h = await ensureFolder(false);
        if (h) { folderRef.current = h; setFolderOn(true); }
      } else if (localStorage.getItem("vxf_dest") === "local") {
        setDest("vixelforge");
      }
    })();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line
  }, [open]);

  // Pick the first model of the current mode whenever the catalog or the mode changes
  useEffect(() => {
    if (!catalog) return;
    const list = isVideo ? catalog.video : catalog.image;
    if (!list?.length || list.some((m) => m.id === modelId)) return;
    const first = list.find((m) => !isLocked(m)) || list[0];
    setModelId(first.id);
    if (isVideo) { setDuration(first.default_duration); setAspect(first.default_aspect || ""); setAudio(false); }
    else setSize(pickSize(first, initialAspect));
    setResolution(first.default_resolution || "");
    // eslint-disable-next-line
  }, [catalog, isVideo]);

  const onSelectModel = (m) => {
    setModelId(m.id);
    if (isVideo) {
      setDuration(m.default_duration);
      setAspect(m.default_aspect || "");
      setResolution(m.default_resolution || "");
      setAudio(false);
    } else {
      setSize(pickSize(m, initialAspect));
      setResolution(m.default_resolution || "");
    }
    setResult(null);
  };

  const connect = async () => {
    try {
      const h = await connectFolder();
      folderRef.current = h;
      setFolderOn(true);
      toast.success(tt.folder_connected);
      return h;
    } catch { return null; }
  };

  // Must run inside a click gesture: re-grants permission on the saved folder or asks to pick one.
  const ensureLocalFolder = async () => {
    if (folderRef.current) {
      try { if ((await folderRef.current.queryPermission({ mode: "readwrite" })) === "granted") return folderRef.current; } catch { void 0; }
    }
    const h = await ensureFolder(true);
    if (h) { folderRef.current = h; setFolderOn(true); return h; }
    return await connect();
  };

  const selectLocal = async () => {
    if (!supported) return;
    setDest("local");
    if (!folderOn) await ensureLocalFolder();
  };

  const saveImageLocally = async (gen) => {
    if (!folderRef.current) return false;
    try {
      const blob = dataUrlToBlob(gen.image_data_url);
      const ext = (gen.mime.split("/")[1] || "png").replace("jpeg", "jpg");
      const path = await saveToFolder(folderRef.current, { type: "image", filename: `${gen.generation_id}.${ext}`, blob });
      await api.patch(`/generation/${gen.generation_id}`, { saved: true, storage_mode: "local", local_path: path });
      return true;
    } catch { return false; }
  };

  const saveVideoLocally = async (genId, blob) => {
    if (!folderRef.current) return false;
    try {
      const path = await saveToFolder(folderRef.current, { type: "video", filename: `${genId}.mp4`, blob });
      await api.patch(`/generation/${genId}`, { saved: true, storage_mode: "local", local_path: path });
      return true;
    } catch { return false; }
  };

  const connectDrive = async () => {
    // Open the tab synchronously inside the click gesture, otherwise iOS/Safari
    // blocks the popup once we await the /drive/connect call.
    let win = null;
    try { win = window.open("about:blank", "_blank"); } catch { win = null; }
    try {
      const { data } = await api.post("/drive/connect");
      toast.message(tt.drive_opening);
      if (win && !win.closed) {
        win.location.href = data.authorization_url;
      } else {
        // Popup blocked (typical on mobile Safari) -> full-page redirect in the same tab.
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      if (win && !win.closed) win.close();
      toast.error(errorMessage(err));
    }
  };

  const uploadToDrive = async (genId, dataUrl) => {
    try {
      await api.post("/drive/upload", { generation_id: genId, data_url: dataUrl || undefined });
      return { ok: true };
    } catch (err) { return { ok: false, msg: errorMessage(err) }; }
  };

  // Refresh Drive connection status when the user returns from the Google consent tab
  useEffect(() => {
    if (!open) return;
    const onFocus = async () => {
      try {
        const { data: ds } = await api.get("/drive/status");
        setDrive((prev) => { if (ds.connected && !prev.connected) setDest("drive"); return ds; });
      } catch { /* ignore */ void 0; }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line
  }, [open]);

  const generateImage = async () => {
    let { data } = await api.post("/generation/images", {
      model: modelId, prompt: promptLocal, size, resolution: resolution || undefined, prompt_id: promptIdLocal || undefined,
      reference_images: imgRefs.slice(0, model?.max_refs || 0).map((r) => ({ kind: r.kind, id: r.id })),
      ref_mode: refMode,
      dest,
    });
    // Slow models (e.g. GPT Image 2.5 Sunburst) finish in background: poll until the job is done.
    const startedAt = Date.now();
    while (data.status === "processing") {
      if (!mountedRef.current || Date.now() - startedAt > 10 * 60 * 1000) { setLoading(false); return; }
      await new Promise((r) => setTimeout(r, 4000));
      let j;
      try { ({ data: j } = await api.get(`/generation/image-jobs/${data.generation_id}`)); }
      catch (e) {
        if (e?.response?.status === 404) {
          // Job state lost (server restart): recover from the stored generation if it finished.
          const { data: g } = await api.get(`/generation/${data.generation_id}`);
          if (g.status !== "completed" || !g.has_media) throw e;
          const blob = (await api.get(`/generation/${data.generation_id}/download`, { responseType: "blob" })).data;
          const dataUrl = await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(blob); });
          j = { status: "completed", generation_id: g.generation_id, image_data_url: dataUrl, mime: blob.type, storage_mode: g.storage_mode, credits_available: credits - cost };
        } else if (e?.response) throw e;
        else continue;
      }
      if (j.status === "failed") throw { response: { data: { detail: j.error } } };
      if (j.status === "completed") data = j;
    }
    setCredits(data.credits_available);
    setResult({ type: "image", image_data_url: data.image_data_url, generation_id: data.generation_id, mime: data.mime });
    toast.success(tt.generated_ok);
    if (dest === "vixelforge") {
      toast[data.storage_mode === "vixelforge" ? "success" : "error"](data.storage_mode === "vixelforge" ? tt.saved_vixelforge : tt.save_failed);
    } else if (dest === "drive" && drive.connected) {
      const res = await uploadToDrive(data.generation_id, data.image_data_url);
      if (res.ok) toast.success(tt.saved_drive);
      else toast.error(res.msg || tt.save_failed);
    } else if (dest === "local") {
      const ok = await saveImageLocally(data);
      toast[ok ? "success" : "error"](ok ? tt.saved_local : tt.save_failed);
    }
    setLoading(false);
  };

  const pollVideo = (genId, startedAt = Date.now()) => {
    pollRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      if (Date.now() - startedAt > 32 * 60 * 1000) {
        toast.error(tt.failed);
        try { const { data: m } = await api.get("/generation/models"); setCredits(m.credits_available || 0); } catch { void 0; }
        setLoading(false);
        return;
      }
      let data;
      try { ({ data } = await api.get(`/generation/${genId}`)); } catch { pollVideo(genId, startedAt); return; }
      try {
        if (data.status === "completed") {
          await finishVideo(genId, data);
        } else if (data.status === "failed") {
          toast.error(tt.failed);
          const { data: m } = await api.get("/generation/models");
          setCredits(m.credits_available || 0);
          setLoading(false);
        } else {
          pollVideo(genId, startedAt);
        }
      } catch { setLoading(false); }
    }, 4000);
  };

  // The job is done server-side: stop the spinner, then fetch + save with bounded retries (never re-poll).
  const finishVideo = async (genId, data) => {
    setLoading(false);
    toast.success(tt.generated_vid_ok);
    let blob = null;
    for (let i = 0; i < 4 && !blob; i++) {
      try { blob = (await api.get(`/generation/${genId}/download`, { responseType: "blob", timeout: 180000 })).data; }
      catch { await new Promise((r) => setTimeout(r, 3000 * (i + 1))); }
    }
    setResult({ type: "video", generation_id: genId, objUrl: blob ? URL.createObjectURL(blob) : null, blob, needsSave: false });
    if (dest === "vixelforge") {
      toast[data.storage_mode === "vixelforge" ? "success" : "error"](data.storage_mode === "vixelforge" ? tt.saved_vixelforge : tt.save_failed);
    } else if (dest === "drive" && drive.connected) {
      const r = await uploadToDrive(genId, null);
      if (r.ok) toast.success(tt.saved_drive);
      else toast.error(r.msg || tt.save_failed);
    } else if (dest === "local") {
      if (!blob) { toast.error(tt.vid_fetch_failed); return; }
      const ok = await saveVideoLocally(genId, blob);
      if (ok) toast.success(tt.saved_local);
      else { toast.error(tt.save_failed); setResult((r) => r && { ...r, needsSave: true }); }
    }
  };

  // Click gesture: re-grant folder permission (browsers revoke it in the background) and retry the save.
  const retrySaveLocal = async () => {
    if (!result?.blob || !(await ensureLocalFolder())) return;
    const ok = await saveVideoLocally(result.generation_id, result.blob);
    toast[ok ? "success" : "error"](ok ? tt.saved_local : tt.save_failed);
    if (ok) setResult((r) => r && { ...r, needsSave: false });
  };

  const frameBody = (f) => (f ? { kind: f.kind, id: f.id } : undefined);
  const useStart = model?.supports_start_frame ? startFrame : null;
  const endOk = model?.supports_end_frame && (!variant || variant.end_frame);
  const useEnd = endOk && useStart ? endFrame : null;

  const generateVideo = async () => {
    const { data } = await api.post("/generation/videos", {
      model: modelId, prompt: promptLocal, duration, aspect: aspect || undefined, resolution: resolution || undefined, prompt_id: promptIdLocal || undefined,
      dest, start_frame: frameBody(useStart), end_frame: frameBody(useEnd), audio: model?.supports_audio ? audio : undefined,
    });
    // Uploaded / reference frames are deleted server-side after generation.
    if (useStart && useStart.kind !== "gallery") setStartFrame(null);
    if (useEnd && useEnd.kind !== "gallery") setEndFrame(null);
    setCredits(data.credits_available);
    pollVideo(data.generation_id);
  };

  const generate = async () => {
    if (!model) return;
    if (promptLocal.trim().length < 3) { toast.error(tt.prompt_required); return; }
    if (dest === "drive" && !drive.connected) { toast.error(tt.connect_drive); return; }
    if (credits < cost) { toast.error(tt.not_enough); return; }
    if (dest === "local" && !(await ensureLocalFolder())) { toast.error(tt.folder_connect); return; }
    setLoading(true); setResult(null);
    try {
      if (isVideo) await generateVideo();
      else await generateImage();
    } catch (err) {
      toast.error(errorMessage(err));
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    if (result.type === "image") {
      a.href = result.image_data_url;
      a.download = `${result.generation_id}.${(result.mime.split("/")[1] || "png").replace("jpeg", "jpg")}`;
    } else {
      a.href = result.objUrl;
      a.download = `${result.generation_id}.mp4`;
    }
    a.click();
  };

  const body = (
        <div className="grid md:grid-cols-2 gap-6 py-2">
          {/* Controls */}
          <div className="space-y-4">
            {editablePrompt && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setVidMode(false)} data-testid="ext-mode-image"
                    className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${!vidMode ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    {tt.mode_image}
                  </button>
                  <button onClick={() => setVidMode(true)} data-testid="ext-mode-video"
                    className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${vidMode ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    {tt.mode_video}
                  </button>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.prompt_label}</label>
                  <textarea value={promptLocal} onChange={(e) => setPromptLocal(e.target.value)} rows={4} placeholder={tt.prompt_ph}
                    data-testid="ext-prompt-input"
                    className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 p-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50 resize-y" />
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.engine}</span>
              <span className="text-xs text-orange-300 font-bold flex items-center gap-1"><Coins className="w-3 h-3" /> {credits} <span className="text-zinc-500 font-normal">{tt.credits}</span></span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {models.map((m) => (
                <button key={m.id} onClick={() => onSelectModel(m)} data-testid={`gen-model-${m.id}`} disabled={isLocked(m)}
                  className={`text-left p-2.5 rounded-lg border transition-colors ${isLocked(m) ? "opacity-45 cursor-not-allowed bg-white/[0.02] border-white/10" : modelId === m.id ? "bg-orange-500/10 border-orange-500/40" : "bg-white/5 border-white/10 hover:border-white/25"}`}>
                  <div className="text-xs font-semibold">{m.name}</div>
                  <div className="text-[10px] text-zinc-500">{m.provider}</div>
                  <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-orange-300">
                    <Coins className="w-3 h-3" /> {isVideo ? videoCost(m, m.default_duration, m.default_resolution) : m.credits} {(isVideo ? videoCost(m, m.default_duration, m.default_resolution) : m.credits) === 1 ? tt.credit_one : tt.credits_word}
                  </div>
                  {isLocked(m) && <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-400" data-testid={`gen-model-locked-${m.id}`}><Lock className="w-3 h-3" /> {tt.trial_locked}</div>}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {isVideo ? (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.duration}</label>
                  <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="gen-duration-select"
                    className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-orange-500/50">
                    {durationOpts.map((d) => <option key={d} value={d} className="bg-zinc-900">{d.replace("s", "")}{tt.seconds}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.aspect}</label>
                  <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="gen-size-select"
                    className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-orange-500/50">
                    {(model?.sizes || []).map((s) => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
                  </select>
                </div>
              )}
              {isVideo && model?.aspect && (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.aspect}</label>
                  <select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="gen-aspect-select"
                    className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-orange-500/50">
                    {model.aspect.map((a) => <option key={a} value={a} className="bg-zinc-900">{a}</option>)}
                  </select>
                </div>
              )}
              {model?.resolutions && (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.resolution}</label>
                  <select value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="gen-resolution-select"
                    className="mt-1.5 w-full rounded-lg bg-white/5 border border-white/10 p-2 text-sm text-white focus:outline-none focus:border-orange-500/50">
                    {model.resolutions.map((r) => {
                      const v = isVideo ? model.res_variants?.[r] : null;
                      const locked = v?.frame_only && !startFrame;
                      return <option key={r} value={r} disabled={locked} className="bg-zinc-900">{r}{locked ? ` (${tt.needs_start})` : ""}</option>;
                    })}
                  </select>
                </div>
              )}
            </div>

            {!isVideo && model && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.refs}</span>
                <div className="mt-1.5">
                  <RefPicker refs={imgRefs} setRefs={setImgRefs} max={model.max_refs || 0} tt={tt} />
                  {imgRefs.length > 0 && <div className="mt-3"><RefModePicker value={refMode} onChange={setRefMode} modes={["edit", "new_scene", "style", "retouch"]} /></div>}
                </div>
              </div>
            )}

            {isVideo && model && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.frames}</span>
                <div className="mt-1.5">
                  <FramePicker start={startFrame} end={endFrame} setStart={(f) => { setStartFrame(f); if (!f) setEndFrame(null); }} setEnd={setEndFrame}
                    supportsStart={model.supports_start_frame} supportsEnd={endOk} tt={tt} />
                </div>
              </div>
            )}

            {isVideo && model?.supports_audio && (
              <div data-testid="audio-card">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.audio_label}</span>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button type="button" onClick={() => setAudio(false)} data-testid="audio-off-btn"
                    className={`py-2 rounded-lg text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${!audio ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    <VolumeX className="w-3.5 h-3.5" /> {tt.audio_off}{(model.audio_mult || 1) !== 1 ? ` · ${videoCost(model, duration, resolution, false)}` : ""}
                  </button>
                  <button type="button" onClick={() => setAudio(true)} data-testid="audio-on-btn"
                    className={`py-2 rounded-lg text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${audio ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    <Volume2 className="w-3.5 h-3.5" /> {tt.audio_on}{(model.audio_mult || 1) !== 1 ? ` · ${videoCost(model, duration, resolution, true)}` : ""}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5">{(model.audio_mult || 1) !== 1 ? tt.audio_hint : tt.audio_hint_free}</p>
              </div>
            )}

            <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-3" data-testid="storage-card">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{tt.dest}</span>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  <button onClick={() => setDest("vixelforge")} data-testid="dest-vixelforge"
                    className={`py-1.5 rounded-lg text-xs font-semibold border transition-colors ${dest === "vixelforge" ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    {tt.dest_vixelforge}
                  </button>
                  <button onClick={selectLocal} data-testid="dest-local" disabled={!supported}
                    className={`py-1.5 rounded-lg text-xs font-semibold border transition-colors ${dest === "local" ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"} ${!supported ? "opacity-40 cursor-not-allowed" : ""}`}>
                    {tt.dest_local}
                  </button>
                  <button onClick={() => setDest("drive")} data-testid="dest-drive"
                    className={`py-1.5 rounded-lg text-xs font-semibold border transition-colors ${dest === "drive" ? "bg-orange-500 text-black border-orange-500" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/25"}`}>
                    {tt.dest_drive}
                  </button>
                </div>
              </div>

              {dest === "vixelforge" ? (
                <p className="text-[10px] text-zinc-500 flex items-start gap-1">
                  <Cloud className="w-3 h-3 mt-0.5 shrink-0 text-orange-400" /> {tt.dest_vixelforge_hint}
                </p>
              ) : dest === "drive" ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-xs">
                    {drive.connected ? <Cloud className="w-4 h-4 text-green-400" /> : <CloudOff className="w-4 h-4 text-zinc-400" />}
                    <span className={drive.connected ? "text-green-400 font-medium" : "text-zinc-300"}>
                      {drive.connected ? (tt.drive_connected + (drive.email ? ` · ${drive.email}` : "")) : tt.connect_drive}
                    </span>
                  </span>
                  {!drive.connected && <Button size="sm" variant="outline" onClick={connectDrive} data-testid="connect-drive-btn" className="border-white/15 bg-transparent hover:bg-white/10 h-7 text-xs">{tt.connect_drive}</Button>}
                </div>
              ) : (
                <>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-xs">
                  {folderOn ? <FolderCheck className="w-4 h-4 text-green-400" /> : <FolderOpen className="w-4 h-4 text-zinc-400" />}
                  <span className={folderOn ? "text-green-400 font-medium" : "text-zinc-300"} data-testid="folder-status">{folderOn ? `${tt.folder_connected}${folderRef.current?.name ? ` · ${folderRef.current.name}` : ""}` : tt.folder_connect}</span>
                </span>
                {supported && <Button size="sm" variant="outline" onClick={connect} data-testid="gen-connect-folder-btn" className="border-white/15 bg-transparent hover:bg-white/10 h-7 text-xs">{tt.folder_connect}</Button>}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5">
                {supported ? tt.folder_hint : <span className="flex items-start gap-1 text-amber-400/90"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {tt.folder_unsupported}</span>}
              </p>
                </>
              )}
            </div>

            {trial && (
              <div className="rounded-lg border border-orange-500/25 bg-orange-500/[0.06] p-3 text-xs text-zinc-300 flex items-start justify-between gap-3" data-testid="gen-trial-note">
                <span>{isVideo ? tt.trial_video : tt.trial_note.replace("{d}", trial.days)}</span>
                <Link to="/app/account" className="shrink-0 font-bold text-orange-300 hover:text-orange-200" data-testid="gen-trial-cta">{tt.trial_cta}</Link>
              </div>
            )}
            <Button onClick={generate} disabled={loading || !model || trialBlocked || promptLocal.trim().length < 3} data-testid="gen-dialog-generate-btn"
              className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold h-11">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isVideo ? tt.generating_vid : tt.generating}</> : <><Sparkles className="w-4 h-4 mr-2" /> {tt.generate} · {cost} {cost === 1 ? tt.credit_one : tt.credits_word}</>}
            </Button>
          </div>

          {/* Result */}
          <div className="space-y-2">
            <div className="rounded-xl border border-white/10 bg-[#0E0E12] aspect-square flex items-center justify-center overflow-hidden" data-testid="gen-dialog-result">
              {loading ? <div className="flex flex-col items-center gap-2 text-zinc-500 px-4 text-center"><Loader2 className="w-7 h-7 animate-spin" /><span className="text-xs">{isVideo ? tt.generating_vid : tt.generating}</span></div>
                : result?.type === "image" ? <img src={result.image_data_url} alt="result" className="w-full h-full object-contain" data-testid="gen-result-image" />
                : result?.type === "video" ? (result.objUrl
                  ? <video src={result.objUrl} controls autoPlay loop className="w-full h-full object-contain" data-testid="gen-result-video" />
                  : <Link to={`/gallery?open=${result.generation_id}`} className="text-xs text-orange-400 underline px-4 text-center" data-testid="gen-result-open-gallery">{tt.vid_in_gallery}</Link>)
                : <Sparkles className="w-8 h-8 text-zinc-700" />}
            </div>
            {result?.needsSave && (
              <Button onClick={retrySaveLocal} data-testid="gen-retry-save-folder-btn" className="w-full bg-orange-500 hover:bg-orange-600 text-black h-9 text-sm">
                <FolderOpen className="w-4 h-4 mr-2" /> {tt.save_to_folder}
              </Button>
            )}
            {result && (result.type === "image" || result.objUrl) && (
              <Button variant="outline" onClick={download} data-testid="gen-download-btn" className="w-full border-white/15 bg-transparent hover:bg-white/10 h-9 text-sm">
                <Download className="w-4 h-4 mr-2" /> {tt.download}
              </Button>
            )}
            {promptIdLocal && <p className="text-[10px] text-zinc-500 text-center">{tt.linked}</p>}
          </div>
        </div>
  );

  if (inline) return <div className="text-white" data-testid="generate-studio">{body}</div>;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && pollRef.current) clearTimeout(pollRef.current); onOpenChange(v); }}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-orange-400" /> {isVideo ? tt.title_vid : tt.title_img}
          </DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
